import React from 'react';
import isNil from 'lodash/isNil';
import Skeleton from 'core/components/Skeleton';
import Button from 'pages/Dashboard/components/Button';
import Dialog from 'pages/Dashboard/components/Dialog';
import InsuranceBalanceExplanationContent from 'pages/Dashboard/pages/Ledger/InsuranceBalanceExplanation/Explanation';
import {
  useGetInsuranceBalanceExplanation,
} from 'pages/Dashboard/pages/Ledger/hooks';

export type InsuranceBalanceExplanationDialogParams = {
  encounterId: number;
};

type Props = InsuranceBalanceExplanationDialogParams & {
  onClose: () => void;
};

export const CardSkeleton = () => (
  <div className="flex flex-col w-full gap-1 flex-1">
    <div className="flex items-center justify-between divide-x divide-gray-200 mt-3 mb-1">
      <div className="flex flex-col gap-0.5 px-3 first:pl-0 last:pr-0">
        <Skeleton className="!w-24 !h-3" />
        <Skeleton className="!w-20 !h-4" />
      </div>
      <div className="flex flex-col gap-0.5 px-3 first:pl-0 last:pr-0">
        <Skeleton className="!w-24 !h-3" />
        <Skeleton className="!w-20 !h-4" />
      </div>
      <div className="flex flex-col gap-0.5 px-3 first:pl-0 last:pr-0">
        <Skeleton className="!w-24 !h-3" />
        <Skeleton className="!w-20 !h-4" />
      </div>
    </div>

    <div className="border-t border-gray-100" />

    <div className="flex flex-col gap-0.5 py-2">
      <Skeleton className="!w-32 !h-3" />
      <Skeleton className="!w-full !h-3" />
      <Skeleton className="!w-5/6 !h-3" />
    </div>

    <div className="border-t border-gray-100" />

    <div className="flex flex-col gap-1 pt-1 divide-y">
      <div className="flex flex-col gap-1 pt-1">
        <div className="flex items-center gap-2">
          <Skeleton className="!w-32 !h-3" />
        </div>
        <div className="flex items-start justify-between p-2 pl-0 gap-2 py-0.5">
          <div className="flex flex-col gap-0.5 flex-1">
            <div className="flex items-center gap-1.5">
              <Skeleton className="!w-1.5 !h-1.5 !rounded-full shrink-0" />
              <Skeleton className="!w-44 !h-3" />
            </div>
            <Skeleton className="!w-3/4 !h-3 pl-3" />
          </div>
          <Skeleton className="!w-16 !h-3" />
        </div>
        <div className="flex items-start justify-between p-2 pl-0 gap-2 py-0.5">
          <div className="flex flex-col gap-0.5 flex-1">
            <div className="flex items-center gap-1.5">
              <Skeleton className="!w-1.5 !h-1.5 !rounded-full shrink-0" />
              <Skeleton className="!w-40 !h-3" />
            </div>
            <Skeleton className="!w-2/3 !h-3 pl-3" />
          </div>
          <Skeleton className="!w-16 !h-3" />
        </div>
      </div>
    </div>
  </div>
);

export default function InsuranceBalanceExplanationDialog({
  encounterId,
  onClose,
}: Props) {
  const {
    data,
    isLoading,
  } = useGetInsuranceBalanceExplanation(encounterId, true);

  const loadedBody = isNil(data) ? null : <InsuranceBalanceExplanationContent data={data} />;
  const body = isLoading ? <CardSkeleton /> : loadedBody;

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth={false}
      className="!w-[600px] min-h-[400px] max-h-[calc(100vh-10rem)] overflow-hidden !space-y-0"
      title="Insurance Balance Explanation"
      titleClassName="mb-2"
    >
      <div className="flex flex-col gap-3 h-full flex-1 min-h-0">
        <div className="flex flex-col flex-1 min-h-0 overflow-y-auto -m-5 p-5">
          {body}
        </div>
        <div className="flex flex-col gap-4 bg-white">
          {!isLoading && (
            <div className="w-full bg-blue-50 border-l-1 border-blue-500 text-slate-700 text-xs font-normal p-2 rounded-md">
              If you disagree with this insurance balance, send an email to
              {' '}
              <span className="text-blue-600 font-semibold">support@populate.com</span>
              {' '}
              with Patient and Date of Service details.
            </div>
          )}
          <div className="flex justify-end">
            <Button
              variant="outlined"
              type="button"
              onClick={onClose}
            >
              Close
            </Button>
          </div>
        </div>
      </div>
    </Dialog>
  );
}
